import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { h200Compiled } from '@confluence/typography-placeholder';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import type { SpaceSelectorProps } from '@confluence/space-selector';
import { SpaceSelector } from '@confluence/space-selector';

export const i18n = defineMessages({
	spaceSelectorLabel: {
		id: 'template-panel.space-selector.label',
		defaultMessage: 'Select a template from this space',
		description:
			'Label text above a space selector. The space selector is used as a filter for templates listed under it.',
	},
});

const spaceSelectorWrapperStyles = xcss({
	marginBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpaceSelectorLabel = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	...h200Compiled,
	marginTop: token('space.150', '12px'),
	marginBottom: token('space.050', '4px'),
});

// A space must include at least one of these permissions in order for us to list it in the Template Panel
const requiredSpaceOperations = [
	{
		targetType: 'page',
		operation: 'create',
	},
	{
		targetType: 'blogpost',
		operation: 'create',
	},
];

type TemplatePanelSpaceSelectorProps = {
	onChange: SpaceSelectorProps['onChange'];
	spaceKey: string;
	isDisabled: boolean;
};

export const TemplatePanelSpaceSelector: FC<TemplatePanelSpaceSelectorProps> = ({
	onChange,
	spaceKey,
	isDisabled,
}) => {
	return (
		<ErrorBoundary attribution={Attribution.TAILORED_EXPERIENCES}>
			<Box xcss={spaceSelectorWrapperStyles}>
				<SpaceSelectorLabel>
					<FormattedMessage {...i18n.spaceSelectorLabel} />
				</SpaceSelectorLabel>
				<SpaceSelector
					onChange={onChange}
					spaceKey={spaceKey}
					isDisabled={isDisabled}
					operationsUsedToDetermineRestrictedStatus={requiredSpaceOperations}
					showRestrictedSpaces={false}
					filterDefaultFromResults={false}
					showSpaceLabel={false}
					filterStarredFromRecent
					selectSpacing="compact"
				/>
			</Box>
		</ErrorBoundary>
	);
};
